import type {
  FieldEntityTarget,
  FieldOrigin,
  GetCustomFieldsResponse,
  GetCustomFieldResponse,
  AddCustomFieldRequestPayload,
  NewCustomFieldStateResponse,
  UpdateCustomFieldRequestPayload,
  SetCustomFieldsOrderRequestPayload,
  GetFieldConfigurationsResponse,
  SetFieldConfigRequestPayload,
  SetFieldConfigResponse,
  ResetFieldConfigResponse,
  GetCustomFieldValuesResponse,
  PatchCustomFieldValuesRequestPayload,
  PatchCustomFieldValuesResponse
} from '@gat-ship/types'
import { useGsCloudBackend } from '@gat-ship/http-client'

export const FIELD_CONFIGURATION_API_PATHS = {
  getCustomFields: 'fields/custom/definitions',
  addCustomField: 'fields/custom/definitions',
  getCustomField: (id: string) => `fields/custom/definitions/${id}`,
  updateCustomField: (id: string) => `fields/custom/definitions/${id}`,
  deleteCustomField: (id: string) => `fields/custom/definitions/${id}`,
  setCustomFieldsOrder: (target: FieldEntityTarget) =>
    `fields/custom/definitions/${target}/set-order`,
  getFieldConfigurations: (target: FieldEntityTarget) =>
    `fields/configurations/${target}`,
  setFieldConfig: (
    target: FieldEntityTarget,
    origin: FieldOrigin,
    fieldName: string
  ) => `fields/configurations/${target}/${origin}/${fieldName}`,
  resetFieldConfig: (
    target: FieldEntityTarget,
    origin: FieldOrigin,
    fieldName: string
  ) => `fields/configurations/${target}/${origin}/${fieldName}/reset`,
  customFieldValues: (target: FieldEntityTarget, entityId: string) =>
    `fields/custom/values/${target}/${entityId}`
}

export function useFieldConfigurationApiCalls() {
  const { apiGet, apiPost, apiPut, apiPatch, apiDelete } = useGsCloudBackend()

  /** Gets all custom field definitions for a specific entity target */
  async function getCustomFieldsRequest(target: FieldEntityTarget) {
    return await apiGet<GetCustomFieldsResponse>(
      FIELD_CONFIGURATION_API_PATHS.getCustomFields,
      { params: { target } }
    )
  }

  /** Gets a single custom field definition by ID */
  async function getCustomFieldRequest(id: string) {
    return await apiGet<GetCustomFieldResponse>(
      FIELD_CONFIGURATION_API_PATHS.getCustomField(id)
    )
  }

  /** Adds a new custom field for a specific entity type */
  async function addCustomFieldRequest(
    payload: AddCustomFieldRequestPayload,
    commandId: string
  ) {
    return await apiPost<
      NewCustomFieldStateResponse,
      AddCustomFieldRequestPayload
    >(FIELD_CONFIGURATION_API_PATHS.addCustomField, payload, commandId)
  }

  /** Updates an existing custom field definition */
  async function updateCustomFieldRequest(
    id: string,
    payload: UpdateCustomFieldRequestPayload,
    commandId: string
  ) {
    return await apiPut<
      NewCustomFieldStateResponse,
      UpdateCustomFieldRequestPayload
    >(FIELD_CONFIGURATION_API_PATHS.updateCustomField(id), payload, commandId)
  }

  /** Removes (soft deletes) a custom field definition */
  async function deleteCustomFieldRequest(id: string, commandId: string) {
    return await apiDelete<undefined>(
      FIELD_CONFIGURATION_API_PATHS.deleteCustomField(id),
      undefined,
      commandId
    )
  }

  /** Sets the display order of custom fields for a target */
  async function setCustomFieldsOrderRequest(
    target: FieldEntityTarget,
    payload: SetCustomFieldsOrderRequestPayload,
    commandId: string
  ) {
    return await apiPut<undefined, SetCustomFieldsOrderRequestPayload>(
      FIELD_CONFIGURATION_API_PATHS.setCustomFieldsOrder(target),
      payload,
      commandId
    )
  }

  /** Gets all field configurations (overrides) for a specific target */
  async function getFieldConfigurationsRequest(target: FieldEntityTarget) {
    return await apiGet<GetFieldConfigurationsResponse>(
      FIELD_CONFIGURATION_API_PATHS.getFieldConfigurations(target)
    )
  }

  /** Sets custom UI behavior (overrides) for a specific field */
  async function setFieldConfigRequest(
    target: FieldEntityTarget,
    origin: FieldOrigin,
    fieldName: string,
    payload: SetFieldConfigRequestPayload,
    commandId: string
  ) {
    return await apiPut<SetFieldConfigResponse, SetFieldConfigRequestPayload>(
      FIELD_CONFIGURATION_API_PATHS.setFieldConfig(target, origin, fieldName),
      payload,
      commandId
    )
  }

  /** Resets field configuration to defaults */
  async function resetFieldConfigRequest(
    target: FieldEntityTarget,
    origin: FieldOrigin,
    fieldName: string,
    commandId: string
  ) {
    return await apiPut<ResetFieldConfigResponse>(
      FIELD_CONFIGURATION_API_PATHS.resetFieldConfig(target, origin, fieldName),
      undefined,
      commandId
    )
  }

  /** Retrieves custom field values for a specific entity */
  async function getCustomFieldValuesRequest(
    target: FieldEntityTarget,
    entityId: string
  ) {
    return await apiGet<GetCustomFieldValuesResponse>(
      FIELD_CONFIGURATION_API_PATHS.customFieldValues(target, entityId)
    )
  }

  /** Patches custom field values for a specific entity */
  async function patchCustomFieldValuesRequest(
    target: FieldEntityTarget,
    entityId: string,
    payload: PatchCustomFieldValuesRequestPayload,
    commandId: string
  ) {
    return await apiPatch<
      PatchCustomFieldValuesResponse,
      PatchCustomFieldValuesRequestPayload
    >(
      FIELD_CONFIGURATION_API_PATHS.customFieldValues(target, entityId),
      payload,
      commandId
    )
  }

  return {
    getCustomFieldsRequest,
    getCustomFieldRequest,
    addCustomFieldRequest,
    updateCustomFieldRequest,
    deleteCustomFieldRequest,
    setCustomFieldsOrderRequest,
    getFieldConfigurationsRequest,
    setFieldConfigRequest,
    resetFieldConfigRequest,
    getCustomFieldValuesRequest,
    patchCustomFieldValuesRequest
  }
}
